import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Crown,
  Activity,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownLeft,
  Sparkles,
  Building2,
  Users,
  PiggyBank,
  Coins,
  ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { fmtINR, fmtPct, fmtUnits, shortDate, longDate } from "../../lib/format";
import { computeMemberMetrics, xirr } from "../../lib/xirr";

/* ───────────────────────── shared utils ───────────────────────── */

function getFiscalYear(dateStr) {
  // Indian FY: 1 Apr → 31 Mar. Returns "FY 2025-2026" style.
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = d.getMonth(); // 0-11
  const startYr = m >= 3 ? y : y - 1;
  return `FY ${startYr}-${startYr + 1}`;
}

function getQuarterKey(dateStr) {
  const d = new Date(dateStr);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()} Q${q}`;
}

function classifyMember(name) {
  if (!name) return "Individual";
  const n = name.toLowerCase();
  if (n.includes("llp")) return "LLP";
  if (n.includes("pvt ltd") || n.includes("pvt. ltd")) return "Pvt Ltd";
  if (n.includes("foundation") || n.includes("fund") || n.includes("(fgi)") || n.includes("(fhf)"))
    return "Family Fund";
  return "Individual";
}

const TYPE_PALETTE = {
  Individual: "#10b981",
  LLP: "#6366f1",
  "Pvt Ltd": "#f59e0b",
  "Family Fund": "#ec4899",
};

/* ───────────────────────── #1 Leaderboard ribbon ───────────────────────── */

export function LeaderboardRibbon({ members, transactions }) {
  const data = useMemo(() => {
    if (!members?.length) return null;

    const biggest = [...members].sort(
      (a, b) => (b.holdingPct || 0) - (a.holdingPct || 0),
    )[0];

    // Top performer: compute XIRR for each and pick max (require >= 0.1y to filter noise)
    const enriched = members
      .map((m) => ({ ...m, _m: computeMemberMetrics(m, transactions) }))
      .filter((m) => m._m?.years && m._m.years >= 0.1 && m._m.xirr != null);
    const topPerf = enriched.length
      ? enriched.sort((a, b) => (b._m.xirr || 0) - (a._m.xirr || 0))[0]
      : null;

    // Most recent activity
    const lastTx = (transactions || [])
      .filter((t) => t.type !== "bonus")
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const lastMember = lastTx ? members.find((m) => m.id === lastTx.memberId) : null;

    return { biggest, topPerf, lastTx, lastMember };
  }, [members, transactions]);

  if (!data || !data.biggest) return null;

  return (
    <div
      data-testid="leaderboard-ribbon"
      className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4"
    >
      <RibbonChip
        testId="ribbon-biggest"
        memberId={data.biggest.id}
        icon={Crown}
        eyebrow="Biggest holder"
        title={data.biggest.name}
        value={fmtPct(data.biggest.holdingPct || 0)}
        sub={fmtINR(data.biggest.currentValue || 0, { compact: true })}
        tone="amber"
      />
      <RibbonChip
        testId="ribbon-top-perf"
        memberId={data.topPerf?.id}
        icon={Activity}
        eyebrow="Top performer (XIRR)"
        title={data.topPerf?.name || "—"}
        value={data.topPerf ? fmtPct(data.topPerf._m.xirr) : "—"}
        sub={data.topPerf ? `over ${data.topPerf._m.years} yrs` : "no data"}
        tone={
          data.topPerf && data.topPerf._m.xirr >= 0 ? "emerald" : "rose"
        }
      />
      <RibbonChip
        testId="ribbon-recent"
        memberId={data.lastMember?.id}
        icon={Clock}
        eyebrow="Most recent activity"
        title={data.lastMember?.name || "—"}
        value={data.lastTx ? shortDate(data.lastTx.date) : "—"}
        sub={
          data.lastTx
            ? `${data.lastTx.type === "subscription" ? "subscribed" : data.lastTx.type === "redemption" ? "redeemed" : "bonus"} ${fmtINR(
                data.lastTx.fundsCr || data.lastTx.fundsDr || 0,
                { compact: true },
              )}`
            : "no transactions"
        }
        tone="sky"
      />
    </div>
  );
}

function RibbonChip({ testId, memberId, icon: Icon, eyebrow, title, value, sub, tone }) {
  const toneRing = {
    amber: "from-amber-500/10 to-transparent ring-amber-500/30",
    emerald: "from-emerald-500/10 to-transparent ring-emerald-500/30",
    sky: "from-sky-500/10 to-transparent ring-sky-500/30",
    rose: "from-rose-500/10 to-transparent ring-rose-500/30",
  }[tone];
  const toneText = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    sky: "text-sky-300",
    rose: "text-rose-300",
  }[tone];

  const inner = (
    <div
      className={`group holo-border scan-sheen bg-gradient-to-br ${toneRing} rounded-xl p-4 ring-1 transition-transform hover:-translate-y-0.5`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-[10px] font-mono uppercase tracking-[0.18em] ${toneText} flex items-center gap-1.5`}>
            <Icon size={11} /> {eyebrow}
          </div>
          <div className="mt-2 text-sm md:text-base font-semibold text-white truncate" title={title}>
            {title}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-400 font-mono truncate">{sub}</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-xl md:text-2xl font-display tabular-nums ${toneText}`}>{value}</div>
        </div>
      </div>
      {memberId && (
        <div className="mt-2 text-[10px] text-slate-500 font-mono flex items-center gap-1 group-hover:text-slate-300 transition-colors">
          Open detail <ChevronRight size={10} className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      )}
    </div>
  );

  if (memberId) return <Link to={`/members/${memberId}`}>{inner}</Link>;
  return inner;
}

/* ───────────────────────── #5 Capital velocity ───────────────────────── */

export function CapitalVelocity({ transactions }) {
  const data = useMemo(() => {
    const byQ = {};
    for (const t of transactions || []) {
      if (t.type === "bonus") continue;
      const q = getQuarterKey(t.date);
      if (!byQ[q]) byQ[q] = { quarter: q, inflow: 0, outflow: 0, net: 0 };
      if ((t.fundsCr || 0) > 0) byQ[q].inflow += t.fundsCr;
      if ((t.fundsDr || 0) > 0) byQ[q].outflow += t.fundsDr;
    }
    const arr = Object.values(byQ)
      .map((r) => ({ ...r, net: r.inflow - r.outflow }))
      .sort((a, b) => a.quarter.localeCompare(b.quarter));
    return arr.slice(-4); // last 4 quarters
  }, [transactions]);

  const totalNet = data.reduce((s, r) => s + r.net, 0);
  const positive = totalNet >= 0;

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="capital-velocity"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Capital velocity
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">Net flow (last 4 Q)</div>
        </div>
        <div
          className={`text-sm font-mono tabular-nums ${positive ? "text-emerald-300" : "text-rose-300"}`}
        >
          {positive ? "+" : ""}
          {fmtINR(totalNet, { compact: true })}
        </div>
      </div>
      <div className="mt-3 h-36">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="quarter" stroke="#475569" fontSize={9} tickLine={false} axisLine={false} />
            <YAxis
              stroke="#475569"
              fontSize={9}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => fmtINR(v, { compact: true, showSymbol: false })}
              width={40}
            />
            <Tooltip
              cursor={{ fill: "#1e293b40" }}
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #1e293b",
                borderRadius: 8,
                fontSize: 11,
                color: "#e2e8f0",
              }}
              formatter={(v) => fmtINR(v)}
            />
            <Bar dataKey="net" radius={[4, 4, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.net >= 0 ? "#10b981" : "#f43f5e"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ───────────────────────── #8 Pool metrics: avg tenure + pool XIRR ───────────────────────── */

export function PoolMetrics({ members, transactions, summary }) {
  const data = useMemo(() => {
    if (!members?.length) return null;
    const now = new Date();

    // Capital-weighted avg tenure
    let weightedYears = 0;
    let totalCapital = 0;
    for (const m of members) {
      const txs = (transactions || []).filter((t) => t.memberId === m.id);
      if (txs.length === 0) continue;
      const first = txs.sort((a, b) => new Date(a.date) - new Date(b.date))[0];
      const years = (now - new Date(first.date)) / (365.25 * 24 * 3600 * 1000);
      const cap = m.capitalInvested || 0;
      weightedYears += years * cap;
      totalCapital += cap;
    }
    const avgTenure = totalCapital > 0 ? weightedYears / totalCapital : 0;

    // Pool XIRR — combine all member cashflows + current pool value
    const flows = (transactions || [])
      .filter((t) => t.type !== "bonus")
      .map((t) => ({
        date: new Date(t.date),
        amount:
          (t.fundsCr || 0) > 0 ? -(t.fundsCr || 0) : t.fundsDr || 0,
      }));
    const totalValue = members.reduce(
      (s, m) => s + (m.overallCurrentValue != null ? m.overallCurrentValue : m.currentValue || 0),
      0,
    );
    flows.push({ date: now, amount: totalValue });
    const rate = xirr(flows);
    const poolXirr = rate != null ? rate * 100 : null;

    return { avgTenure, poolXirr, totalValue, totalCapital };
  }, [members, transactions]);

  if (!data) return null;
  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="pool-metrics"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
        Pool maturity
      </div>
      <div className="text-sm font-semibold text-white mt-0.5">Tenure & money-weighted return</div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Avg tenure</div>
          <div className="mt-1 text-lg md:text-xl font-display text-white tabular-nums">
            {data.avgTenure.toFixed(2)} yrs
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 font-mono">capital-weighted</div>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Pool XIRR</div>
          <div
            className={`mt-1 text-lg md:text-xl font-display tabular-nums ${
              data.poolXirr == null
                ? "text-slate-400"
                : data.poolXirr >= 0
                ? "text-emerald-400"
                : "text-rose-400"
            }`}
          >
            {data.poolXirr == null ? "—" : fmtPct(data.poolXirr)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 font-mono">across all members</div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── #10 NAV growth attribution ───────────────────────── */

export function NavAttribution({ members, summary }) {
  const data = useMemo(() => {
    if (!members?.length) return null;

    const totalCapital = members.reduce((s, m) => s + (m.capitalInvested || 0), 0);
    const totalCapitalValue = members.reduce((s, m) => s + (m.currentValue || 0), 0);
    const totalBonusValue = members.reduce((s, m) => s + (m.bonusValue || 0), 0);

    if (totalCapital <= 0) return null;

    const marketGain = totalCapitalValue - totalCapital;        // ₹
    const bonusGain = totalBonusValue;                            // ₹
    const totalGain = marketGain + bonusGain;
    const totalReturnPct = (totalGain / totalCapital) * 100;

    return {
      marketGain,
      bonusGain,
      totalGain,
      totalReturnPct,
      totalCapital,
      marketShare: totalGain !== 0 ? (marketGain / totalGain) * 100 : 0,
      bonusShare: totalGain !== 0 ? (bonusGain / totalGain) * 100 : 0,
    };
  }, [members]);

  if (!data) return null;
  const positive = data.totalReturnPct >= 0;

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="nav-attribution"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Total return attribution
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">Where the growth came from</div>
        </div>
        <div
          className={`font-mono tabular-nums text-base ${positive ? "text-emerald-300" : "text-rose-300"}`}
        >
          {positive ? "+" : ""}
          {data.totalReturnPct.toFixed(2)}%
        </div>
      </div>

      {/* 2-segment bar */}
      <div className="mt-4 flex h-3 rounded-full overflow-hidden bg-slate-800">
        <div
          className="bg-emerald-500"
          style={{ width: `${Math.max(0, Math.min(100, Math.abs(data.marketShare)))}%` }}
          title={`Market gain: ${fmtINR(data.marketGain)}`}
        />
        <div
          className="bg-amber-500"
          style={{ width: `${Math.max(0, Math.min(100, Math.abs(data.bonusShare)))}%` }}
          title={`Bonus gain: ${fmtINR(data.bonusGain)}`}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <div className="min-w-0">
            <div className="text-slate-400 font-mono">Market gain</div>
            <div className="text-white font-mono tabular-nums truncate">
              {fmtINR(data.marketGain, { compact: true })}{" "}
              <span className="text-slate-500">({data.marketShare.toFixed(0)}%)</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <div className="min-w-0">
            <div className="text-slate-400 font-mono">Bonus gain</div>
            <div className="text-white font-mono tabular-nums truncate">
              {fmtINR(data.bonusGain, { compact: true })}{" "}
              <span className="text-slate-500">({data.bonusShare.toFixed(0)}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── #3 Vintage / cohort chart ───────────────────────── */

export function VintageChart({ transactions }) {
  const data = useMemo(() => {
    const byFY = {};
    for (const t of transactions || []) {
      if (t.type !== "subscription") continue;
      const fy = getFiscalYear(t.date);
      if (!byFY[fy]) byFY[fy] = { fy, total: 0, count: 0 };
      byFY[fy].total += t.fundsCr || 0;
      byFY[fy].count += 1;
    }
    return Object.values(byFY).sort((a, b) => a.fy.localeCompare(b.fy));
  }, [transactions]);

  const grand = data.reduce((s, r) => s + r.total, 0);

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="vintage-chart"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Capital vintage
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">
            ₹ contributed per fiscal year
          </div>
        </div>
        <div className="text-xs font-mono text-slate-400">
          Total subs: <span className="text-white">{fmtINR(grand, { compact: true })}</span>
        </div>
      </div>
      <div className="mt-4 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="fy" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
            <YAxis
              stroke="#475569"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => fmtINR(v, { compact: true, showSymbol: false })}
              width={55}
            />
            <Tooltip
              cursor={{ fill: "#1e293b40" }}
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #1e293b",
                borderRadius: 8,
                fontSize: 11,
                color: "#e2e8f0",
              }}
              formatter={(v, n, p) => [
                fmtINR(v),
                `${p.payload.count} subs`,
              ]}
            />
            <Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ───────────────────────── #9 Family vs Individual composition ───────────────────────── */

export function CompositionDonut({ members }) {
  const data = useMemo(() => {
    const groups = {};
    for (const m of members || []) {
      const t = classifyMember(m.name);
      if (!groups[t]) groups[t] = { name: t, value: 0, count: 0, color: TYPE_PALETTE[t] };
      groups[t].value += m.currentValue || 0;
      groups[t].count += 1;
    }
    return Object.values(groups)
      .filter((g) => g.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [members]);

  const total = data.reduce((s, g) => s + g.value, 0);

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="composition-donut"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
        Pool composition
      </div>
      <div className="text-sm font-semibold text-white mt-0.5">Individuals vs entities</div>

      <div className="mt-3 grid grid-cols-5 gap-3 items-center">
        <div className="col-span-2 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius="55%"
                outerRadius="90%"
                paddingAngle={2}
                stroke="#0f172a"
                strokeWidth={2}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: 8,
                  fontSize: 11,
                  color: "#e2e8f0",
                }}
                formatter={(v) => fmtINR(v)}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="col-span-3 space-y-1.5">
          {data.map((g) => {
            const pct = total > 0 ? (g.value / total) * 100 : 0;
            return (
              <div key={g.name} className="flex items-center gap-2 text-[11px]">
                <span className="w-2 h-2 rounded-full" style={{ background: g.color }} />
                <div className="text-slate-300 flex-1 truncate">{g.name}</div>
                <div className="text-slate-500 font-mono">{g.count}</div>
                <div className="text-white font-mono tabular-nums w-12 text-right">{pct.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── #4 Recent activity feed ───────────────────────── */

export function RecentActivityFeed({ transactions, members }) {
  const items = useMemo(() => {
    const memberMap = Object.fromEntries((members || []).map((m) => [m.id, m]));
    return (transactions || [])
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10)
      .map((t) => ({ t, m: memberMap[t.memberId] }));
  }, [transactions, members]);

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="recent-activity"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Recent activity
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">Last 10 transactions</div>
        </div>
      </div>
      <div className="mt-3 divide-y divide-slate-800">
        {items.length === 0 && (
          <div className="text-xs text-slate-500 py-8 text-center">No transactions yet.</div>
        )}
        {items.map(({ t, m }) => {
          const isBonus = t.type === "bonus";
          const isSub = !isBonus && (t.fundsCr || 0) > 0;
          const amount = t.fundsCr || t.fundsDr || 0;
          const Icon = isBonus ? Sparkles : isSub ? ArrowUpRight : ArrowDownLeft;
          const color = isBonus
            ? "text-amber-400"
            : isSub
            ? "text-emerald-400"
            : "text-rose-400";
          return (
            <Link
              to={m ? `/members/${m.id}` : "#"}
              key={t.id || `${t.date}-${t.memberId}`}
              className="flex items-center gap-3 py-2.5 hover:bg-slate-800/40 -mx-2 px-2 rounded transition-colors"
            >
              <div
                className={`w-7 h-7 rounded-md flex items-center justify-center border ${
                  isBonus
                    ? "bg-amber-500/10 border-amber-500/30"
                    : isSub
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-rose-500/10 border-rose-500/30"
                }`}
              >
                <Icon size={13} className={color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">
                  <span className="font-medium">{m?.name || "Member"}</span>{" "}
                  <span className="text-slate-400">
                    {isBonus
                      ? "received bonus"
                      : isSub
                      ? "subscribed"
                      : "redeemed"}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  {shortDate(t.date)} · NAV ₹{Number(t.nav || 0).toFixed(4)}
                </div>
              </div>
              <div className={`text-sm font-mono tabular-nums ${color}`}>
                {isSub || isBonus ? "+" : "−"}
                {fmtINR(amount, { compact: true })}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── #6 Top movers ───────────────────────── */

export function TopMovers({ members }) {
  const data = useMemo(() => {
    const enriched = (members || [])
      .filter((m) => (m.capitalInvested || 0) > 0)
      .map((m) => ({
        ...m,
        _pct: m.overallGainPct != null ? m.overallGainPct : m.gainLossPct || 0,
        _gain:
          m.overallGainLoss != null ? m.overallGainLoss : m.gainLoss || 0,
      }));
    const winners = [...enriched]
      .filter((m) => m._pct >= 0)
      .sort((a, b) => b._pct - a._pct)
      .slice(0, 3);
    const losers = [...enriched]
      .filter((m) => m._pct < 0)
      .sort((a, b) => a._pct - b._pct)
      .slice(0, 3);
    return { winners, losers };
  }, [members]);

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="top-movers"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
        Top movers
      </div>
      <div className="text-sm font-semibold text-white mt-0.5">Winners & losers (overall %)</div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <MoverList title="Winners" items={data.winners} positive />
        <MoverList title="Losers" items={data.losers} positive={false} />
      </div>
    </div>
  );
}

function MoverList({ title, items, positive }) {
  const color = positive ? "text-emerald-400" : "text-rose-400";
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{title}</div>
      <div className="mt-1 space-y-1">
        {items.length === 0 && (
          <div className="text-[11px] text-slate-500 italic">—</div>
        )}
        {items.map((m) => (
          <Link
            to={`/members/${m.id}`}
            key={m.id}
            className="flex items-center justify-between gap-2 text-xs py-1 px-1.5 -mx-1.5 rounded hover:bg-slate-800/50 transition-colors"
          >
            <div className="min-w-0 text-slate-200 truncate">{m.name}</div>
            <div className={`font-mono tabular-nums flex items-center gap-0.5 ${color}`}>
              <Icon size={10} />
              {fmtPct(m._pct)}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────── #7 Member chip strip ───────────────────────── */

export function MemberChipStrip({ members }) {
  const sorted = useMemo(
    () =>
      (members || [])
        .slice()
        .sort((a, b) => (b.holdingPct || 0) - (a.holdingPct || 0)),
    [members],
  );

  if (!sorted.length) return null;

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
      data-testid="member-chip-strip"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            All members at a glance
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">
            {sorted.length} members · sorted by holding %
          </div>
        </div>
        <div className="text-[10px] text-slate-500 font-mono flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/> Gain</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500"/> Flat</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500"/> Loss</span>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {sorted.map((m) => {
          const pct =
            m.overallGainPct != null ? m.overallGainPct : m.gainLossPct || 0;
          const inv = (m.capitalInvested || 0) > 0;
          const tone = !inv
            ? "border-slate-700 bg-slate-900 text-slate-500"
            : pct >= 5
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/15"
            : pct >= 0
            ? "border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
            : "border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/15";
          return (
            <Link
              to={`/members/${m.id}`}
              key={m.id}
              className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border text-xs transition-colors ${tone}`}
              title={`${m.name} · ${fmtINR(m.currentValue || 0)} · ${fmtPct(m.holdingPct || 0)}`}
              data-testid={`member-chip-${m.id}`}
            >
              <span className="font-medium truncate max-w-[160px]">{m.name}</span>
              <span className="text-[10px] font-mono tabular-nums opacity-80">
                {inv ? fmtPct(pct) : "—"}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
